import { Stack, Label, IconButton, Panel, PanelType, PrimaryButton, MessageBar, MessageBarType } from '@fluentui/react';
import _ from 'lodash';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { createAddress, editAddress } from '../../../api/accounts';
import { HealAPI } from '../../../healApi';
import { Address } from '../../../api/address.types';
import { getFullAddressString } from '../../../util/Address';
import { HealHqMap } from '../../core/HealHqMap';
import { AddressEditor } from '../components/AddressEditor';
import { ListItem } from '../components/ListItem';
import { useCart } from '../CartContext';

export function SelectLocation() {
  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Label className="ms-font-xl">Select Location</Label>
      <MapAddress />
      <DisplayAddresses />
    </Stack>
  );
}

function MapAddress() {
  const { state: cart } = useCart();

  let mapCenter = { lat: 34.0522, lng: -118.2437 };
  const mapPoints = [];
  if (!!cart.where.addressId) {
    mapCenter = { lat: cart.where.latitude, lng: cart.where.longitude };
    mapPoints.push(mapCenter);
  }

  return <HealHqMap height={300} center={mapCenter} points={mapPoints} />;
}

function DisplayAddresses() {
  const { state: cart, dispatch } = useCart();

  const addresses: Address[] = cart.who.account.addresses;

  const homeAddress = addresses.find((a) => a.addressType === 'HOME') || ({ addressType: 'HOME' } as Address);
  const workAddress = addresses.find((a) => a.addressType === 'WORK') || ({ addressType: 'WORK' } as Address);
  const otherAddress = addresses.find((a) => a.addressType === 'OTHER') || ({ addressType: 'OTHER' } as Address);

  const onClick = useCallback(
    (selectedAddress: Address) => {
      if (!selectedAddress.addressId) {
        dispatch({ type: 'set_where', data: {} }); // clear it
      } else {
        // .replace(/,[^,]*$/,"") removes the last comma-separated segment from the string, which is the country abbreviation.
        // The country isn't shown in the list items, so it makes sense for this to do the same.
        dispatch({
          type: 'set_where',
          data: {
            addressId: selectedAddress.addressId,
            address: getFullAddressString(selectedAddress).replace(/,[^,]*$/, ''),
            latitude: selectedAddress.latitude,
            longitude: selectedAddress.longitude,
          },
        });
      }
    },
    [dispatch]
  );

  return (
    <Stack root={{ style: { width: 500 } }}>
      <Stack horizontal>
        <ListItem
          title="Home"
          iconName="Home"
          value={!!homeAddress.addressId ? `${homeAddress.streetAddress}${homeAddress.unit ? `, ${homeAddress.unit}` : ''}` : ''}
          value2={!!homeAddress.addressId ? `${homeAddress.city}, ${homeAddress.state} ${homeAddress.zipcode}` : ''}
          active={!!cart.where.addressId && homeAddress.addressId === cart.where.addressId}
          enabled={!!homeAddress.addressId}
          completed={false}
          testId="select_home"
          onClick={() => onClick(homeAddress)}
        />
        <EditAddress address={homeAddress} />
      </Stack>
      <Stack horizontal>
        <ListItem
          title="Work"
          iconName="Work"
          value={!!workAddress.addressId ? `${workAddress.streetAddress}${workAddress.unit ? `, ${workAddress.unit}` : ''}` : ''}
          value2={!!workAddress.addressId ? `${workAddress.city}, ${workAddress.state} ${workAddress.zipcode}` : ''}
          active={!!cart.where.addressId && workAddress.addressId === cart.where.addressId}
          enabled={!!workAddress.addressId}
          completed={false}
          testId="select_work"
          onClick={() => onClick(workAddress)}
        />
        <EditAddress address={workAddress} />
      </Stack>
      <Stack horizontal>
        <ListItem
          title="Other"
          iconName="MapPin"
          value={!!otherAddress.addressId ? `${otherAddress.streetAddress}${otherAddress.unit ? `, ${otherAddress.unit}` : ''}` : ''}
          value2={!!otherAddress.addressId ? `${otherAddress.city}, ${otherAddress.state} ${otherAddress.zipcode}` : ''}
          active={!!cart.where.addressId && otherAddress.addressId === cart.where.addressId}
          enabled={!!otherAddress.addressId}
          completed={false}
          testId="select_other"
          onClick={() => onClick(otherAddress)}
        />
        <EditAddress address={otherAddress} />
      </Stack>
    </Stack>
  );
}

function EditAddress({ address }: { address: Address }) {
  const { state: cart, dispatch } = useCart();
  const [open, setOpen] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<Address>(address);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAddressToEdit(address);
  }, [address]);

  const canSaveAddress = useMemo(() => {
    return (
      !saving &&
      !_.isEqual(address, addressToEdit) &&
      !!addressToEdit?.streetAddress &&
      !!addressToEdit?.addressType &&
      !!addressToEdit?.city &&
      addressToEdit?.zipcode.length === 5
    );
  }, [address, saving, addressToEdit]);

  const saveAddress = useCallback(async () => {
    if (addressToEdit) {
      setSaving(true);
      setError('');
      // Validate coverage
      const coverage = await HealAPI.checkAddressCoverage(addressToEdit.latitude, addressToEdit.longitude).then((r) => r.data);
      if (!coverage.covered) {
        setError('Address is not in a Heal coverage area.');
        setSaving(false);
        return;
      }

      let address: Address;
      if (addressToEdit.addressId) {
        address = await editAddress(addressToEdit);
        dispatch({ type: 'set_address', data: address });
      } else {
        address = await createAddress(cart.who.accountId, addressToEdit);
        dispatch({ type: 'set_address', data: address });
      }

      // .replace(/,[^,]*$/,"") removes the last comma-separated segment from the string, which is the country abbreviation.
      // The country isn't shown in the list items, so it makes sense for this to do the same.
      dispatch({
        type: 'set_where',
        data: { addressId: address.addressId, address: getFullAddressString(address).replace(/,[^,]*$/, ''), latitude: address.latitude, longitude: address.longitude },
      });
      setSaving(false);
      setOpen(false);
    }
  }, [dispatch, cart.who.accountId, addressToEdit]);

  const onDismiss = useCallback(() => {
    setAddressToEdit(address);
    setOpen(false);
  }, [address]);

  return (
    <>
      <IconButton iconProps={{ iconName: 'Edit' }} onClick={() => setOpen(true)} />
      {open && (
        <Panel isOpen={open} onDismiss={onDismiss} type={PanelType.medium} headerText={address.addressId ? 'Update Address' : 'Set Address'}>
          {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
          <AddressEditor address={addressToEdit} onChange={setAddressToEdit} />
          <PrimaryButton style={{ marginTop: 20 }} onClick={saveAddress} disabled={!canSaveAddress} text="Save" data-tid="btn_saveaddress" />
        </Panel>
      )}
    </>
  );
}
